import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Search, ChevronLeft, ChevronRight, Shield, ShieldAlert, Ban, Plus, X, Trash2, UserX, AlertCircle } from 'lucide-react';

const ROLES = [
  { id: 'administrator', label: 'Administrator', icon: Shield, color: '#28a745' },
  { id: 'super_administrator', label: 'Super Administrator', icon: ShieldAlert, color: '#6f42c1' },
  { id: 'blocked', label: 'Blocked', icon: Ban, color: '#dc3545' },
  { id: 'deleted', label: 'Deleted', icon: UserX, color: '#6c757d' }
];

const RoleBadge = ({ role, onRemove }) => {
  const roleInfo = ROLES.find(r => r.id === role) || { label: role, color: '#6c757d' };
  const Icon = roleInfo.icon || Shield;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      background: `${roleInfo.color}15`,
      color: roleInfo.color,
      marginRight: '4px'
    }}>
      <Icon size={12} />
      {roleInfo.label}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0 0 0 4px',
            color: roleInfo.color,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <X size={12} />
        </button>
      )}
    </span>
  );
};

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [addingRoleForUser, setAddingRoleForUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { userId, userName, userEmail }
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState(null); // { userId, userName, userEmail }
  const queryClient = useQueryClient();
  const limit = 15;

  const { data, isLoading } = useQuery(
    ['users', page, searchTerm],
    async () => {
      const params = new URLSearchParams({ page, limit });
      if (searchTerm) params.append('search', searchTerm);
      const response = await axios.get(`/api/users?${params}`);
      return response.data;
    },
    { keepPreviousData: true }
  );

  const addRoleMutation = useMutation(
    async ({ userId, role }) => {
      const response = await axios.post(`/api/users/${userId}/roles`, { role });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Role added');
        setAddingRoleForUser(null);
      },
      onError: () => toast.error('Failed to add role')
    }
  );

  const removeRoleMutation = useMutation(
    async ({ userId, role }) => {
      const response = await axios.delete(`/api/users/${userId}/roles/${role}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Role removed');
      },
      onError: () => toast.error('Failed to remove role')
    }
  );

  const deleteUserMutation = useMutation(
    async (userId) => {
      const response = await axios.delete(`/api/users/${userId}`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('users');
        let message = 'User deleted successfully';
        if (data.festivalsUnpublished > 0) {
          message += `. ${data.festivalsUnpublished} festival(s) marked as draft.`;
        }
        toast.success(message);
        setDeleteConfirm(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete user');
        setDeleteConfirm(null);
      }
    }
  );

  const permanentDeleteMutation = useMutation(
    async (userId) => {
      const response = await axios.delete(`/api/users/${userId}/permanent`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('users');
        let message = 'User permanently deleted';
        if (data.festivalsOrphaned > 0) {
          message += `. ${data.festivalsOrphaned} festival(s) now have no creator.`;
        }
        toast.success(message);
        setPermanentDeleteConfirm(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to permanently delete user');
        setPermanentDeleteConfirm(null);
      }
    }
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleDeleteClick = (user) => {
    setDeleteConfirm({ userId: user.id, userName: user.name, userEmail: user.email });
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteUserMutation.mutate(deleteConfirm.userId);
    }
  };

  const handlePermanentDeleteClick = (user) => {
    setPermanentDeleteConfirm({ userId: user.id, userName: user.name, userEmail: user.email });
  };

  const confirmPermanentDelete = () => {
    if (permanentDeleteConfirm) {
      permanentDeleteMutation.mutate(permanentDeleteConfirm.userId);
    }
  };

  const users = data?.users || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 };

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>User Management</h2>

      {/* Search Bar */}
      <form onSubmit={handleSearch} style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#999'
          }} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>
        <button type="submit" className="btn btn-primary">
          Find
        </button>
        {searchTerm && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { setSearchTerm(''); setSearchInput(''); setPage(1); }}
          >
            Clear
          </button>
        )}
      </form>

      {/* Users Table */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading users...
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            {searchTerm ? 'No users found matching your search.' : 'No users found.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Name</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Email</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Joined</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Roles</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', width: '80px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isDeleted = user.roles?.includes('deleted') || user.email.startsWith('DELETED_USER');
                return (
                <tr key={user.id} style={{ borderBottom: '1px solid #e9ecef', opacity: isDeleted ? 0.6 : 1 }}>
                  <td style={{ padding: '12px 16px' }}>{user.name}</td>
                  <td style={{ padding: '12px 16px', color: '#666' }}>{user.email}</td>
                  <td style={{ padding: '12px 16px', color: '#666' }}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                      {user.roles?.map(role => (
                        <RoleBadge
                          key={role}
                          role={role}
                          onRemove={role !== 'deleted' ? () => removeRoleMutation.mutate({ userId: user.id, role }) : undefined}
                        />
                      ))}

                      {!isDeleted && addingRoleForUser === user.id ? (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                addRoleMutation.mutate({ userId: user.id, role: e.target.value });
                              }
                            }}
                            style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}
                          >
                            <option value="">Select role...</option>
                            {ROLES.filter(r => r.id !== 'deleted' && !user.roles?.includes(r.id)).map(role => (
                              <option key={role.id} value={role.id}>{role.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => setAddingRoleForUser(null)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              color: '#999'
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : !isDeleted && (
                        <button
                          onClick={() => setAddingRoleForUser(user.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            border: '1px dashed #ccc',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: '#666'
                          }}
                        >
                          <Plus size={12} />
                          Add
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    {!isDeleted ? (
                      <button
                        onClick={() => handleDeleteClick(user)}
                        title="Delete user"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '4px',
                          color: '#dc3545',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#fee'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePermanentDeleteClick(user)}
                        title="Permanently delete user"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '4px',
                          color: '#dc3545',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#fee'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <Trash2 size={16} />
                        <AlertCircle
                          size={10}
                          style={{
                            position: 'absolute',
                            top: '2px',
                            right: '2px',
                            background: 'white',
                            borderRadius: '50%'
                          }}
                        />
                      </button>
                    )}
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderTop: '1px solid #e9ecef',
            background: '#f8f9fa'
          }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Showing {(pagination.page - 1) * limit + 1} - {Math.min(pagination.page * limit, pagination.total)} of {pagination.total} users
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <span style={{
                padding: '8px 16px',
                background: 'white',
                borderRadius: '6px',
                border: '1px solid #ddd'
              }}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc3545' }}>Delete User?</h3>
            <p style={{ margin: '0 0 8px 0', color: '#333' }}>
              Are you sure you want to delete this user?
            </p>
            <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px' }}>
              <strong>{deleteConfirm.userName}</strong><br />
              {deleteConfirm.userEmail}
            </p>
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '13px' }}>
              This will change their email to DELETED_USER_*, add a "Deleted" role,
              and mark any festivals they exclusively own as draft.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="btn btn-secondary"
                disabled={deleteUserMutation.isLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="btn"
                disabled={deleteUserMutation.isLoading}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: deleteUserMutation.isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {deleteUserMutation.isLoading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Dialog */}
      {permanentDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc3545' }}>⚠️ Permanently Delete User?</h3>
            <p style={{ margin: '0 0 8px 0', color: '#333', fontWeight: '500' }}>
              This action cannot be undone!
            </p>
            <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px' }}>
              <strong>{permanentDeleteConfirm.userName}</strong><br />
              {permanentDeleteConfirm.userEmail}
            </p>
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '13px' }}>
              This will permanently remove the user from the database.
              All festivals created by this user will have their creator set to "Unknown".
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setPermanentDeleteConfirm(null)}
                className="btn btn-secondary"
                disabled={permanentDeleteMutation.isLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmPermanentDelete}
                className="btn"
                disabled={permanentDeleteMutation.isLoading}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: permanentDeleteMutation.isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {permanentDeleteMutation.isLoading ? 'Deleting...' : 'Yes, Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
